import "./SubscribeButton.scss";

import { Alert, Button, Flex } from "@fluentui/react-northstar";
import FormState, { FormData, validate, validators } from "@shopify/react-form-state";
import { TeamsSubscriptionPlans } from "@teams/types";
import React, { useState } from "react";

import { useUserInfo } from "../../actions/useUserInfo";
import { isError } from "../../services/ChannelApi";
import { userApi } from "../../services/UserApi";
import { useTeamsContext } from "../../state/TeamsContextProvider";
import { clientEventTracker } from "../../utils/clientTelemetry";
import { isValidEmail } from "../../validators";
import { TextInputField } from "../fields";
import { PollyDialog } from "../PollyDialog";
import { Congratulations } from "./Congratulations";

export type RequestLicenseButtonProps = {
  fluid?: boolean;
  plan: TeamsSubscriptionPlans;
  onDone: () => void;
};

type ContactDetails = {
  emailAddress: string;
  reason: string;
};

export const RequestLicenseButton: React.FC<RequestLicenseButtonProps> = ({
  plan,
  onDone,
  fluid,
}) => {
  const { teamsContext } = useTeamsContext();
  const { user } = useUserInfo();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [trialStartError, setTrialStartError] = useState<Error>();
  const [showContactForm, setShowContactForm] = useState<boolean>(false);

  async function requestTrial(formDetails: FormData<ContactDetails>): Promise<void | Error> {
    setIsLoading(true);
    const result = await userApi.startTrial(
      teamsContext,
      plan,
      formDetails.fields.emailAddress.value,
      formDetails.fields.reason.value
    );

    if (isError(result)) {
      clientEventTracker.trackFatalError("trialrequest/error", user, {
        error: result,
        plan,
      });
      setTrialStartError(result);
    } else {
      setShowSuccess(true);
    }
    setIsLoading(false);
  }

  if (trialStartError) {
    return (
      <PollyDialog
        width="90vw"
        maxWidth="700px"
        header="Request License"
        onCancel={() => {
          onDone();
        }}
        closeOnOutsideClick={false}
        open={!!trialStartError}
      >
        <Alert danger content={`Unable to start a trial: ${trialStartError.message}`} />;
      </PollyDialog>
    );
  }

  if (showSuccess) {
    return (
      <PollyDialog
        width="90vw"
        maxWidth="700px"
        header="Request License"
        onCancel={() => {
          setShowSuccess(false);
          onDone();
        }}
        closeOnOutsideClick={false}
        open={showSuccess}
      >
        <Congratulations
          plan={plan}
          onClose={() => {
            onDone();
          }}
          licenseRequest
        />
      </PollyDialog>
    );
  }

  if (showContactForm) {
    return <ContactForm requestTrial={requestTrial} onClose={() => setShowContactForm(false)} />;
  }

  return (
    <>
      <Button
        fluid={fluid}
        primary
        className="usermenu__menu_button"
        data-tname={"request-license"}
        content="Request License"
        disabled={isLoading}
        onClick={() => {
          setShowContactForm(true);
          clientEventTracker.track("RequestLicense clicked", user, {
            upn: user.upn,
          });
        }}
      />
    </>
  );
};

const ContactForm: React.FC<{
  requestTrial: (formDetails: FormData<ContactDetails>) => Promise<void | Error>;
  onClose: () => void;
}> = ({ requestTrial, onClose }) => {
  const initialValues: ContactDetails = {
    emailAddress: "",
    reason: "",
  };

  const contactValidators = {
    emailAddress: [
      validators.requiredString("A valid email is required"),
      validate(isValidEmail, "A valid email is required"),
    ],
    reason: [validators.requiredString("Please add a reason for the license request")],
  };

  return (
    <PollyDialog
      width="65vw"
      maxWidth="500px"
      onCancel={() => {
        onClose();
      }}
      closeOnOutsideClick={false}
      open={true}
    >
      <FormState
        initialValues={initialValues}
        onSubmit={(formDetails: FormData<ContactDetails>) => {
          requestTrial(formDetails);
          onClose();
        }}
        validators={contactValidators}
        validateOnSubmit
      >
        {({ fields, submitting, submit }) => {
          return (
            <Flex gap="gap.medium" column>
              <TextInputField
                field={fields.emailAddress}
                label="Where can we send this request for approval? You can also provide your IT helpdesk email if you aren't sure."
                readOnly={submitting}
                fluid
                placeholder="Enter email address"
                required
              />

              <TextInputField
                field={fields.reason}
                label="Reason for Polly license request"
                readOnly={submitting}
                fluid
                placeholder="e.g. Need more responses to run meeting polls"
              />

              <Flex hAlign="end" gap="gap.small">
                <Button content="Cancel" onClick={() => onClose()} />
                <Button
                  primary
                  content="Submit"
                  onClick={async e => {
                    e.preventDefault();
                    submit();
                  }}
                  loader={submitting}
                />
              </Flex>
            </Flex>
          );
        }}
      </FormState>
    </PollyDialog>
  );
};
